import { ChangeDetectionStrategy, Component, effect, inject, input, Signal, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLinkWithHref } from '@angular/router';
import {
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonRouterLinkWithHref,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginLogoComponent, ScreensizeService } from '@okcargo-apps/layout-ionic';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { atLeastOneCheckboxCheckedValidator, detailedEmailValidator, phoneUEValidator } from '@okcargo-apps/utils';
import { CompanyRegistrationService } from '@okcargo-apps/shared-infrastructure';
import { mapFormsToCompanyRegistrationRequest } from '../company-registration.mappers';
import {
  CompanyRegistrationCompanyFormModel,
  CompanyRegistrationUserFormModel,
} from '../company-registration-forms.model';
import { companyPostalCodeValidator, cityMinLengthValidator } from '../company-registration.validators';
import { CompanyRegistrationFormComponent } from '../company-registration-form/company-registration-form.component';
import { UserRegistrationFormComponent } from '../user-registration-form/user-registration-form.component';
import { ICompanyRegistrationRequest } from '@okcargo-apps/shared-domain';
import { RegistrationSuccessModalComponent } from '../registration-success-modal/registration-success-modal.component';

@Component({
  selector: 'lib-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    IonRouterLinkWithHref,
    RouterLinkWithHref,
    IonButton,
    IonItem,
    IonLabel,
    IonList,
    TranslateModule,
    LoginLogoComponent,
    CompanyRegistrationFormComponent,
    UserRegistrationFormComponent,
  ],
})
export class RegistrationFormComponent {
  private readonly companyRegistrationService = inject(CompanyRegistrationService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly screensizeService = inject(ScreensizeService);
  private readonly translate = inject(TranslateService);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly connectivity = inject(ConnectivityService);

  /** Registering a company is a command: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;
  private readonly modalCtrl = inject(ModalController);

  public defaultIsCarrier = input<boolean>(false);
  public defaultIsShipper = input<boolean>(false);

  public companyForm: FormGroup<CompanyRegistrationCompanyFormModel>;
  public userForm: FormGroup<CompanyRegistrationUserFormModel>;

  public isDesktopView: Signal<boolean> = this.screensizeService.isDesktopView;

  public errorMessage = signal<string | null>(null);

  constructor() {
    this.companyForm = this.formBuilder.group<CompanyRegistrationCompanyFormModel>(
      {
        companyTaxId: this.formBuilder.nonNullable.control('', Validators.required),
        companyName: this.formBuilder.nonNullable.control('', Validators.required),
        companyStreet: this.formBuilder.nonNullable.control('', Validators.required),
        companyPostalCode: this.formBuilder.nonNullable.control('', [
          Validators.required,
          companyPostalCodeValidator(),
        ]),
        companyCity: this.formBuilder.nonNullable.control('', [Validators.required, cityMinLengthValidator()]),
        companyCountryCode: this.formBuilder.nonNullable.control('ES', Validators.required),
        isCarrier: this.formBuilder.nonNullable.control(false),
        isShipper: this.formBuilder.nonNullable.control(false),
      },
      { validators: [atLeastOneCheckboxCheckedValidator(['isCarrier', 'isShipper'])] },
    );

    this.userForm = this.formBuilder.group<CompanyRegistrationUserFormModel>({
      firstName: this.formBuilder.nonNullable.control('', Validators.required),
      lastName: this.formBuilder.nonNullable.control('', Validators.required),
      email: this.formBuilder.nonNullable.control('', [Validators.required, detailedEmailValidator()]),
      phone: this.formBuilder.nonNullable.control('', [Validators.required, phoneUEValidator()]),
      privacyPolicyAccepted: this.formBuilder.nonNullable.control(false, Validators.requiredTrue),
      termsAndConditionsAccepted: this.formBuilder.nonNullable.control(false, Validators.requiredTrue),
    });

    effect(() => {
      const isCarrier = this.defaultIsCarrier();
      const isShipper = this.defaultIsShipper();
      if (isCarrier || isShipper) {
        this.companyForm.patchValue({ isCarrier, isShipper });
      }
    });

    // Clean errorMessage when any form is updated
    effect(() => {
      this.companyForm.valueChanges.subscribe(() => this.errorMessage.set(null));
      this.userForm.valueChanges.subscribe(() => this.errorMessage.set(null));
    });
  }

  public async doRegister(): Promise<void> {
    if (this.isOffline()) {
      return;
    }

    if (this.companyForm.invalid || this.userForm.invalid) {
      this.companyForm.markAllAsTouched();
      this.userForm.markAllAsTouched();
      this.errorMessage.set(this.translate.instant('register.error.invalid'));
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    const request = mapFormsToCompanyRegistrationRequest(this.companyForm.getRawValue(), this.userForm.getRawValue());

    try {
      await this.companyRegistrationService.register(request);
    } catch {
      this.errorMessage.set(this.translate.instant('register.error.wrong'));
      return;
    } finally {
      await loading.dismiss();
    }

    this.errorMessage.set(null);
    await this.showSuccessModalAndNavigate(request);
  }

  private async showSuccessModalAndNavigate(request: ICompanyRegistrationRequest): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: RegistrationSuccessModalComponent,
      componentProps: { email: request.email },
      backdropDismiss: false,
    });
    await modal.present();
    await modal.onDidDismiss();
    this.router.navigate(['/login']);
  }
}
